import { LockClosedIcon } from "@heroicons/react/24/solid";
import { useDocumentTitle } from "@uidotdev/usehooks";
import { Link } from "react-router-dom";
import { useUser } from "../auth";
import { BACKEND_URL } from "../constants/globals";
import { ROUTES } from ".";

interface SettingsCardProps {
    label: string;
    description: string;
    disabled: boolean;
    locked: boolean;
    /** In-app route for client-side navigation. */
    to?: string;
    /** External/backend URL; when set, opens in a new tab instead of using `to`. */
    href?: string;
}

const cardClassName = (disabled: boolean, locked: boolean) => `
    w-full h-20 px-6 py-4
    rounded border border-slate-200
    justify-start items-end inline-flex
    ${disabled || locked ? "bg-neutral-200 cursor-default" : "bg-white cursor-pointer hover:shadow-lg"}
`;

const SettingsCardBody = (props: SettingsCardProps) => (
    <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
        <div className="text-ae-blue-900 text-xl font-semibold leading-7 flex items-center gap-2">
            {props.label}
            {props.locked && <LockClosedIcon className="w-4" />}
        </div>
        <div className="self-stretch text-zinc-600 text-sm leading-snug">
            {props.description}
        </div>
    </div>
);

const SettingsCard = (props: SettingsCardProps) => {
    const inactive = props.disabled || props.locked;

    if (props.href) {
        return (
            <a
                href={inactive ? undefined : props.href}
                target="_blank"
                rel="noopener noreferrer"
                className={cardClassName(props.disabled, props.locked)}
            >
                <SettingsCardBody {...props} />
            </a>
        );
    }

    return (
        <Link
            to={inactive ? "" : (props.to ?? "")}
            className={cardClassName(props.disabled, props.locked)}
        >
            <SettingsCardBody {...props} />
        </Link>
    );
};

export const SettingsHomePage = () => {
    useDocumentTitle("Settings - AERSHED");
    const userPermissions = useUser().permissions;

    return (
        <div className="w-full h-screen pt-10 flex flex-col items-center">
            <div className="min-w-[45%]">
                <div className="px-0 pt-8 pb-4">
                    <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                        Settings
                    </h1>
                    <p className="mt-1 text-base text-slate-500">
                        Configure your account and your companies settings
                        through this screen.
                    </p>
                </div>
                <div className="flex flex-col gap-4">
                    <SettingsCard
                        to=""
                        label="Account"
                        description="Change user settings, password and email."
                        disabled={true}
                        locked={false}
                    />
                    <SettingsCard
                        to={ROUTES.SETTINGS_NOTIFICATIONS}
                        label="Notification Settings"
                        description="Change company notification settings and create email groups."
                        disabled={false}
                        locked={userPermissions.notificationsView !== true}
                    />
                    <SettingsCard
                        to={ROUTES.SETTINGS_MOBILE_DEVICES}
                        label="Mobile devices"
                        description="Manage mobile devices connected with your account."
                        disabled={false}
                        locked={userPermissions.mobileAppView !== true}
                    />
                    {userPermissions.externalApiView === true && (
                        <SettingsCard
                            href={`${BACKEND_URL}/api/external/v1/docs/swagger/`}
                            label="API Documentation"
                            description="View the Aerscape external API reference (opens in a new tab)."
                            disabled={false}
                            locked={false}
                        />
                    )}
                </div>
            </div>
            <div className="absolute bottom-0 right-0 text-sm text-gray-400">
                Build number: {__BUILD_DATE__}
            </div>
        </div>
    );
};
